import { labelDefaultMediumStyles, labelDefaultRegularStyles } from '@lifebit-ai/lds'
import styled, { type DefaultTheme } from 'styled-components'

import { ImportRequestScanResult } from 'services/import-requests/enums'

const iconBackgroundByScanResult = (theme: DefaultTheme, scanResult: ImportRequestScanResult): string => {
  switch (scanResult) {
    case ImportRequestScanResult.Clean:
      return theme.color.feedback.success.background
    case ImportRequestScanResult.ThreatDetected:
      return theme.color.feedback.danger.background
    case ImportRequestScanResult.ScanFailed:
      return theme.color.feedback.warning.background
    case ImportRequestScanResult.InProgress:
      return theme.color.feedback.action.subtle
    case ImportRequestScanResult.NotStarted:
      return theme.color.feedback.warning.background
    default:
      return theme.color.feedback.action.subtle
  }
}

const contentColorByScanResult = (theme: DefaultTheme, scanResult: ImportRequestScanResult): string => {
  switch (scanResult) {
    case ImportRequestScanResult.ThreatDetected:
      return theme.color.feedback.danger.default
    case ImportRequestScanResult.ScanFailed:
      return theme.color.feedback.warning.default
    case ImportRequestScanResult.Clean:
    case ImportRequestScanResult.InProgress:
    case ImportRequestScanResult.NotStarted:
    default:
      return theme.color.content.secondary
  }
}

const iconFillColorByScanResult = (theme: DefaultTheme, scanResult: ImportRequestScanResult): string => {
  switch (scanResult) {
    case ImportRequestScanResult.Clean:
      return theme.color.feedback.success.default
    case ImportRequestScanResult.ThreatDetected:
      return theme.color.feedback.danger.default
    case ImportRequestScanResult.ScanFailed:
      return theme.color.feedback.warning.default
    case ImportRequestScanResult.InProgress:
      return theme.color.feedback.action.default
    case ImportRequestScanResult.NotStarted:
      return theme.color.feedback.warning.default
    default:
      return theme.color.content.secondary
  }
}

export const ImportRequestScanDetailsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.size.space['05']};
`

export const ScanHeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.size.space['04']};
  justify-content: flex-start;
  width: 100%;
`

export const IconWrapper = styled.div<{ $scanResult: ImportRequestScanResult }>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding: ${({ theme }) => theme.size.space['03']};
  border-radius: ${({ theme }) => theme.size.border.radius['04']};
  background: ${({ theme, $scanResult }) => iconBackgroundByScanResult(theme, $scanResult)};

  svg {
    width: ${({ theme }) => theme.size.icon.medium};
    height: ${({ theme }) => theme.size.icon.medium};

    path {
      fill: ${({ theme, $scanResult }) => iconFillColorByScanResult(theme, $scanResult)};
    }
  }
`

export const Info = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
`

export const Title = styled.div`
  ${labelDefaultMediumStyles}
  font-weight: ${({ theme }) => theme.font.weight.medium};
  color: ${({ theme }) => theme.color.content.main};
`

export const Message = styled.div<{ $scanResult: ImportRequestScanResult }>`
  ${labelDefaultRegularStyles}
  color: ${({ theme, $scanResult }) => contentColorByScanResult(theme, $scanResult)};
`

export const MetadataGrid = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  column-gap: ${({ theme }) => theme.size.space['06']};
  row-gap: ${({ theme }) => theme.size.space['03']};
  width: 100%;
`

export const ToneValue = styled.span<{ $tone?: 'success' | 'danger' }>`
  ${labelDefaultRegularStyles}
  font-weight: ${({ theme }) => theme.font.weight.medium};
  color: ${({ $tone, theme }) => {
    if ($tone === 'success') {
      return theme.color.feedback.success.default
    }

    if ($tone === 'danger') {
      return theme.color.feedback.danger.default
    }

    return theme.color.content.main
  }};
`
